import React, { useMemo, useRef, useState } from 'react';
import {
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import MUIDataTable from 'mui-datatables';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import isbot from 'isbot';
import DeviceDetector from 'device-detector-js';

// ---------- helpers ----------

const DEVICE_TYPE_MAP = {
  1: '1. Mobile/Tablet',
  2: '2. Personal Computer',
  3: '3. Connected TV',
  4: '4. Phone',
  5: '5. Tablet',
  6: '6. Connected Device',
  7: '7. Set Top Box',
};

const CONNECTION_TYPE_MAP = {
  0: '0. Unknown',
  1: '1. Ethernet',
  2: '2. WIFI',
  3: '3. Cellular Network – Unknown Generation',
  4: '4. Cellular Network – 2G',
  5: '5. Cellular Network – 3G',
  6: '6. Cellular Network – 4G',
};

const GEO_TYPE_MAP = {
  1: '1 - GPS/Location Services',
  2: '2 - IP Address',
  3: '3 - User provided (e.g. registration data)',
};

const VIDEO_PLACEMENT_MAP = {
  1: '1. In-Stream',
  2: '2. In-Banner',
  3: '3. In-Article',
  4: '4. In-Feed',
  5: '5. Interstitial/Slider/Floating',
};

// safe nested getter with simple [index] support (e.g. imp[0].video.w)
const getNestedValue = (obj, path, defaultValue = '') => {
  if (!obj || !path) return defaultValue;
  try {
    const value = path.split('.').reduce((acc, part) => {
      if (!acc) return undefined;
      const match = part.match(/^([^[\]]+)(\[(\d+)\])?$/);
      if (!match) return undefined;
      const [, key, , index] = match;
      let next = acc[key];
      if (next == null) return undefined;
      if (index !== undefined) {
        next = next[Number(index)];
      }
      return next;
    }, obj);
    return value !== undefined && value !== null ? value : defaultValue;
  } catch {
    return defaultValue;
  }
};

// try to normalize input to an array of objects
const normalizeToJsonArray = (raw) => {
  const trimmed = (raw || '').trim();
  if (!trimmed) return [];

  // already array / single object?
  try {
    const parsed = JSON.parse(trimmed);
    if (Array.isArray(parsed)) return parsed;
    if (typeof parsed === 'object') return [parsed];
  } catch {
    // fall through
  }

  // multiple objects concatenated: }{ or } { or weird chars
  const noNewlines = trimmed.replace(/(\r\n|\n|\r)/gm, '');
  const separated = noNewlines
    .replace(/}\s*{/g, '},{')
    .replace(/}﻿{/g, '},{'); // weird zero-width char

  const asArray = `[${separated}]`;
  const parsed = JSON.parse(asArray);
  if (!Array.isArray(parsed)) {
    throw new Error('Parsed data is not an array');
  }
  return parsed;
};

// IFA validation logic
const getIfaValidity = (ifa, make) => {
  if (!ifa || typeof ifa !== 'string') return 'Not found';

  const uuidRegex =
    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

  if (uuidRegex.test(ifa)) {
    if (ifa === '00000000-0000-0000-0000-000000000000') {
      if (make === 'Apple') {
        return '1 (But user opted-out)';
      }
      return '1 (But Make field is wrong)';
    }
    return '1';
  }

  // 32-char raw IDFA-like; try to format
  if (ifa.length === 32) {
    const ifka = [...ifa];
    ifka.splice(8, 0, '-');
    ifka.splice(13, 0, '-');
    ifka.splice(18, 0, '-');
    ifka.splice(23, 0, '-');
    const formatted = ifka.join('');
    if (uuidRegex.test(formatted)) {
      return '1 (Converted)';
    }
    return '0';
  }

  return '0';
};

// scan events and collect all possible JSON paths
const collectFieldPathsFromEvents = (events, sampleLimit = 50, maxFields = 1000) => {
  const paths = new Set();

  const traverse = (obj, prefix) => {
    if (!obj || typeof obj !== 'object') {
      if (prefix) paths.add(prefix);
      return;
    }
    if (paths.size >= maxFields) return;

    if (Array.isArray(obj)) {
      if (!obj.length) return;
      const first = obj[0];
      const newPrefix = prefix ? `${prefix}[0]` : '[0]';
      traverse(first, newPrefix);
      return;
    }

    Object.entries(obj).forEach(([key, value]) => {
      const newPrefix = prefix ? `${prefix}.${key}` : key;
      if (value && typeof value === 'object') {
        traverse(value, newPrefix);
      } else {
        paths.add(newPrefix);
      }
    });
  };

  const count = Math.min(events.length, sampleLimit);
  for (let i = 0; i < count; i++) {
    traverse(events[i], '');
    if (paths.size >= maxFields) break;
  }

  return Array.from(paths)
    .filter(
      (p) =>
        p.startsWith('partnerBidRequest') ||
        p.startsWith('partnerBidResponse'),
    )
    .sort();
};

// Build a single row for the table from one event
const buildBidRow = (event, dynamicFields, deviceDetector) => {
  const br = event.partnerBidRequest || {};
  const resp = event.partnerBidResponse || {};

  const app = br.app || {};
  const site = br.site || {};
  const device = br.device || {};
  const geo = device.geo || {};
  const source = br.source || {};
  const schain = source.ext?.schain || br.ext?.schain || {};
  const nodes = schain.nodes || [];
  const deals = event.partnerEligibleDeals || [];
  const seatbid0 = resp.seatbid?.[0] || {};
  const bid0 = seatbid0.bid?.[0] || {};

  // base fields
  const bundleId = app.bundle ?? 'Not found';
  const appDomain = app.domain ?? 'Not found';
  const siteDomain = site.domain ?? 'Not found';
  const sitePage = site.page ?? 'Not found';
  const sitePublisherName = site.publisher?.name ?? 'Not found';
  const sitePublisherId = site.publisher?.id ?? 'Not found';
  const appId = app.id ?? 'Not found';
  const appName = app.name ?? 'Not found';
  const siteName = site.name ?? 'Not found';
  const appPublisherId = app.publisher?.id ?? 'Not found';
  const appPublisherName = app.publisher?.name ?? 'Not found';
  const storeUrl = app.storeurl ?? 'Not found';

  const deviceTypeRaw = device.devicetype;
  const deviceType =
    DEVICE_TYPE_MAP[deviceTypeRaw] ?? (deviceTypeRaw || 'Not found');

  const isApp = device.ext?.is_app ?? 'Not found';
  const ifa = device.ifa ?? 'Not found';
  const connectionTypeRaw = device.connectiontype;
  const connectionType =
    CONNECTION_TYPE_MAP[connectionTypeRaw] ??
    (connectionTypeRaw || 'Not found');
  const ip = device.ip ?? 'Not found';
  const ipv6 = device.ipv6 ?? '';
  const make = device.make ?? 'Not found';
  const model = device.model ?? 'Not found';
  const os = device.os ?? 'Not found';
  const osv = device.osv ?? '';
  const wopv = br.imp?.[0]?.ext?.wopv ?? 'Not found';

  const userAgent = device.ua ?? 'Not found';
  const isBotFlag = userAgent === 'Not found' ? 'false' : isbot(userAgent).toString();
  const deviceData =
    userAgent === 'Not found'
      ? {}
      : deviceDetector.parse(userAgent) || {};
  const botOrDeviceInformation = JSON.stringify(deviceData);

  const schainComplete =
    schain.complete !== undefined ? schain.complete : 'Not found';

  const formatNode = (node) =>
    node
      ? `Asi: ${node.asi || ''} Hp: ${node.hp || ''} Sid: ${node.sid || ''}`
      : '';

  const node0 = nodes[0] || {};
  const node1 = nodes[1] || {};
  const node2 = nodes[2] || {};
  const node3 = nodes[3] || {};

  const sdk = source.ext?.omidpn || '';
  const sdkVer = source.ext?.omidpv || '';

  const geotypeText = geo.type ? GEO_TYPE_MAP[geo.type] || geo.type : '';
  const country = geo.country || '';
  const city = geo.city || '';
  const lat = geo.lat || '';
  const lon = geo.lon || '';

  const location = `${country} ,${city} lat: ${lat} lon: ${lon} Location Type: ${geotypeText}`;

  // media type
  let placementText = '';
  const imp0 = br.imp?.[0];
  if (imp0?.video) {
    const placementRaw = imp0.video.placement;
    const placement = VIDEO_PLACEMENT_MAP[placementRaw] || placementRaw || '';
    const w = imp0.video.w;
    const h = imp0.video.h;
    if (w && h) {
      placementText = `Video | Size: ${w}x${h} | Placement: ${placement}`;
    } else {
      placementText = `Video | Placement: ${placement}`;
    }
  } else if (imp0?.banner) {
    const w = imp0.banner.w;
    const h = imp0.banner.h;
    if (w && h) {
      placementText = `Banner | Size: ${w}x${h}`;
    } else {
      placementText = 'Banner';
    }
  } else if (imp0?.audio) {
    placementText = 'Audio';
  } else if (imp0?.native) {
    placementText = 'Native';
  } else {
    placementText = '';
  }

  // deals
  const partnerEligibleDeals = deals.join('|') || 'Not Found';

  // BCAT
  const bcat = (br.bcat || []).join('|');

  // response
  const adomain = (bid0.adomain?.[0] || 'Not found').toString();
  const creativeId = (bid0.crid || 'Not found').toString();
  const seatId = (seatbid0.seat || 'Not found').toString();
  const size =
    bid0.w && bid0.h ? `${bid0.w}X${bid0.h}` : 'Not found';
  const price =
    bid0.price && resp.cur
      ? `${parseFloat(bid0.price).toFixed(2)} ${resp.cur}`
      : '';

  // IFA validation
  const ifaValid = getIfaValidity(ifa, make);

  // dynamic fields
  const dynamicValues = {};
  (dynamicFields || []).forEach((path) => {
    if (!path) return;
    dynamicValues[path] = getNestedValue(event, path, '');
  });

  return {
    Placement: placementText,
    BundleId: bundleId,
    SiteName: siteName,
    SiteDomain: siteDomain,
    SitePage: sitePage,
    AppDomain: appDomain,
    AppId: appId,
    AppName: appName,
    SitePublisherId: sitePublisherId,
    SitePublisherName: sitePublisherName,
    AppPublisherId: appPublisherId,
    AppPublisherName: appPublisherName,
    StoreUrl: storeUrl,
    DeviceType: deviceType,
    Location: location,
    ConnectionType: connectionType,
    IsApp: isApp,
    Ifa: ifa,
    IfaValid: ifaValid,
    Wopv: wopv,
    Ip: ip,
    Ipv6: ipv6, // NEW default column
    Make: make,
    Model: model,
    Os: os,
    Osv: osv,
    SDK: sdk,
    SdkVersion: sdkVer,
    UserAgent: userAgent,
    BotUserAgent: isBotFlag,
    BotOrDeviceData: botOrDeviceInformation,
    SchainComplete: schainComplete,
    SchainNode0: formatNode(node0),
    SchainNode1: formatNode(node1),
    SchainNode2: formatNode(node2),
    SchainNode3: formatNode(node3),
    PartnerEligibleDeals: partnerEligibleDeals,
    Bcat: bcat,
    BidResponseMarker: '------',
    Adomain: adomain,
    CreativeId: creativeId,
    SeatId: seatId,
    Size: size,
    Price: price,
    // dynamic
    ...dynamicValues,
  };
};

// Process events slowly in chunks (for huge files)
const processEventsSlow = (
  events,
  dynamicFields,
  deviceDetector,
  onProgress,
  onDone,
) => {
  const total = events.length;
  const batchSize = 100;
  const rows = [];

  const processBatch = (startIndex) => {
    const endIndex = Math.min(startIndex + batchSize, total);
    for (let i = startIndex; i < endIndex; i++) {
      rows.push(buildBidRow(events[i], dynamicFields, deviceDetector));
    }
    onProgress(endIndex / total);

    if (endIndex < total) {
      setTimeout(() => processBatch(endIndex), 0);
    } else {
      onDone(rows);
    }
  };

  processBatch(0);
};

// ---------- main component ----------

export default function BidRequestAnalyser() {
  const [rawBidInput, setRawBidInput] = useState('');
  const [bidRows, setBidRows] = useState([]);
  const [useSlowMode, setUseSlowMode] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);

  const [botInput, setBotInput] = useState('');
  const [botRows, setBotRows] = useState([]);

  // auto-detected fields & selection
  const [availableFields, setAvailableFields] = useState([]);
  const [dynamicFields, setDynamicFields] = useState([]); // selected JSON paths

  const fileInputRef = useRef(null);
  const deviceDetector = useMemo(() => new DeviceDetector(), []);

  // base columns (your original ones) + IPv6
  const baseColumns = useMemo(
    () => [
      { name: 'Placement', label: 'Placement' },
      { name: 'BundleId', label: 'BundleId' },
      { name: 'SiteName', label: 'SiteName' },
      { name: 'SiteDomain', label: 'SiteDomain' },
      { name: 'SitePage', label: 'SitePage' },
      { name: 'AppDomain', label: 'AppDomain' },
      { name: 'AppId', label: 'AppId' },
      { name: 'AppName', label: 'AppName' },
      { name: 'SitePublisherId', label: 'SitePublisherId' },
      { name: 'SitePublisherName', label: 'SitePublisherName' },
      { name: 'AppPublisherId', label: 'AppPublisherId' },
      { name: 'AppPublisherName', label: 'AppPublisherName' },
      { name: 'StoreUrl', label: 'StoreUrl' },
      { name: 'DeviceType', label: 'DeviceType' },
      { name: 'Location', label: 'Location' },
      { name: 'ConnectionType', label: 'ConnectionType' },
      { name: 'IsApp', label: 'IsApp' },
      { name: 'Ifa', label: 'Ifa' },
      { name: 'IfaValid', label: 'IfaValid' },
      { name: 'Wopv', label: 'Wopv / PrescanId' },
      { name: 'Ip', label: 'Ip' },
      { name: 'Ipv6', label: 'Ipv6' }, // NEW
      { name: 'Make', label: 'Make' },
      { name: 'Model', label: 'Model' },
      { name: 'Os', label: 'Os' },
      { name: 'Osv', label: 'Osv' },
      { name: 'SDK', label: 'SDK' },
      { name: 'SdkVersion', label: 'SdkVersion' },
      { name: 'UserAgent', label: 'UserAgent' },
      { name: 'BotUserAgent', label: 'BotUserAgent' },
      { name: 'BotOrDeviceData', label: 'BotOrDeviceData' },
      { name: 'SchainComplete', label: 'SchainComplete' },
      { name: 'SchainNode0', label: 'SchainNode0' },
      { name: 'SchainNode1', label: 'SchainNode1' },
      { name: 'SchainNode2', label: 'SchainNode2' },
      { name: 'SchainNode3', label: 'SchainNode3' },
      { name: 'PartnerEligibleDeals', label: 'PartnerEligibleDeals' },
      { name: 'Bcat', label: 'Bcat' },
      { name: 'BidResponseMarker', label: 'Bid Response' },
      { name: 'Adomain', label: 'Adomain' },
      { name: 'CreativeId', label: 'CreativeId' },
      { name: 'SeatId', label: 'SeatId' },
      { name: 'Size', label: 'Size' },
      { name: 'Price', label: 'Price' },
    ],
    [],
  );

  // merge base + dynamic
  const allColumns = useMemo(() => {
    const dynamicCols = (dynamicFields || []).map((path) => ({
      name: path,
      label: path,
      options: {
        filter: false,
        sort: true,
      },
    }));
    return [...baseColumns, ...dynamicCols];
  }, [baseColumns, dynamicFields]);

  const handleAnalyse = () => {
    try {
      const events = normalizeToJsonArray(rawBidInput);
      if (!events.length) {
        toast.warn('Nothing to analyse – please paste JSON or upload a file.');
        return;
      }

      // auto-detect available fields (from sample)
      const detected = collectFieldPathsFromEvents(events);
      setAvailableFields(detected);

      toast.info('Analysing bid requests...');
      setIsProcessing(true);
      setProgress(0);
      setBidRows([]);

      if (useSlowMode && events.length > 500) {
        processEventsSlow(
          events,
          dynamicFields,
          deviceDetector,
          (p) => setProgress(p),
          (rows) => {
            setBidRows(rows);
            setIsProcessing(false);
            toast.success('Analysis finished.');
          },
        );
      } else {
        const rows = events.map((e) =>
          buildBidRow(e, dynamicFields, deviceDetector),
        );
        setBidRows(rows);
        setIsProcessing(false);
        setProgress(1);
        toast.success('Analysis finished.');
      }
    } catch (err) {
      console.error(err);
      setIsProcessing(false);
      toast.error('Failed to parse JSON. Please check the input.');
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      setRawBidInput(String(e.target.result || ''));
      toast.success('File loaded. Click "Analyse Bid Requests".');
    };
    reader.onerror = () => {
      toast.error('Failed to read file.');
    };
    reader.readAsText(file);
  };

  const handleClear = () => {
    setRawBidInput('');
    setBidRows([]);
    setProgress(0);
    setAvailableFields([]);
    setDynamicFields([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCheckBots = () => {
    const lines = botInput
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean);
    if (!lines.length) {
      setBotRows([]);
      return;
    }

    const rows = lines.map((ua) => {
      const isBot1 = isbot(ua).toString();
      const device = deviceDetector.parse(ua);
      const isBot2 = device.bot ? 'true' : 'false';
      return {
        UserAgent: ua,
        isBot: isBot1,
        isBot2,
        Details: JSON.stringify(device),
      };
    });

    setBotRows(rows);
  };

  return (
    <div>
      <ToastContainer position="top-center" />

      <Typography variant="h5" gutterBottom>
        Bid Request / Response analyser
      </Typography>

      {/* INPUT */}
      <TextField
        variant="outlined"
        multiline
        rows={10}
        value={rawBidInput}
        onChange={(e) => setRawBidInput(e.target.value)}
        placeholder="Insert BidRequest/BidResponse JSON (single object, array, or multiple objects glued together)."
        style={{
          borderRadius: '10px',
          textAlign: 'center',
          width: '100%',
          resize: 'vertical',
        }}
      />

      <br />
      <br />

      <FormControlLabel
        control={
          <Checkbox
            checked={useSlowMode}
            onChange={(e) => setUseSlowMode(e.target.checked)}
            color="primary"
          />
        }
        label="Use slower method (avoid browser crash for very large files)"
      />

      {isProcessing && (
        <Typography variant="body2">
          Processing... {Math.round(progress * 100)}%
        </Typography>
      )}

      <br />

      <Button
        variant="contained"
        color="primary"
        onClick={handleAnalyse}
        disabled={isProcessing}
      >
        Analyse Bid Requests
      </Button>

      <input
        ref={fileInputRef}
        style={{ display: 'none' }}
        id="upload-file"
        name="upload-file"
        type="file"
        onChange={handleFileUpload}
      />

      <label htmlFor="upload-file" style={{ marginLeft: 8 }}>
        <Button color="secondary" variant="contained" component="span">
          Upload File
        </Button>
      </label>

      <Button
        style={{ marginLeft: 8 }}
        variant="outlined"
        onClick={handleClear}
        disabled={isProcessing}
      >
        Clear
      </Button>

      <br />
      <br />

      {/* DYNAMIC COLUMNS CONFIG - AUTO DETECTED FIELDS */}
      <Typography variant="h6" gutterBottom>
        Extra columns (auto-detected fields)
      </Typography>
      <Typography variant="body2" color="textSecondary">
        Fields are detected from your JSON under <code>partnerBidRequest</code>{' '}
        and <code>partnerBidResponse</code>. Select any of them to add as columns.
        <br />
        Tip: after changing selection, click <b>Analyse Bid Requests</b> again to
        refresh the table.
      </Typography>
      <br />

      <FormControl variant="outlined" fullWidth>
        <InputLabel id="extra-fields-label">Extra fields</InputLabel>
        <Select
          labelId="extra-fields-label"
          multiple
          value={dynamicFields}
          onChange={(e) => setDynamicFields(e.target.value)}
          label="Extra fields"
          renderValue={(selected) => (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {selected.map((value) => (
                <Chip key={value} label={value} />
              ))}
            </div>
          )}
        >
          {availableFields.length === 0 && (
            <MenuItem disabled>
              <Typography variant="body2" color="textSecondary">
                No fields detected yet. Paste JSON and run "Analyse Bid Requests"
                first.
              </Typography>
            </MenuItem>
          )}
          {availableFields.map((field) => (
            <MenuItem key={field} value={field}>
              <Checkbox checked={dynamicFields.indexOf(field) > -1} />
              <Typography variant="body2">{field}</Typography>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <br />
      <br />

      {/* MAIN TABLE */}
      <MUIDataTable
        title="Bid Request Analysis"
        data={bidRows}
        columns={allColumns}
        options={{
          filterType: 'checkbox',
          downloadOptions: {
            filename:
              'BidRequests_' + new Date().toISOString().slice(0, 10) + '.csv',
            filterOptions: {
              useDisplayedColumnsOnly: true,
            },
          },
          sort: true,
          sortDirection: 'desc',
          textLabels: {
            body: {
              noMatch: 'Please analyse Bid Requests first',
            },
          },
        }}
      />

      {/* BOT CHECKER */}
      <div style={{ marginTop: 48 }}>
        <Typography variant="h5" gutterBottom>
          Bot Checker
        </Typography>

        <TextField
          variant="outlined"
          multiline
          rows={10}
          value={botInput}
          onChange={(e) => setBotInput(e.target.value)}
          placeholder="Insert User-Agent strings (one per line) to determine if these are bot UAs."
          style={{
            borderRadius: '10px',
            textAlign: 'center',
            width: '100%',
            resize: 'vertical',
          }}
        />

        <br />
        <br />

        <Button variant="contained" color="primary" onClick={handleCheckBots}>
          Check Bots
        </Button>

        <br />
        <br />

        <MUIDataTable
          title="Bot checker"
          data={botRows}
          columns={[
            { name: 'UserAgent', label: 'User-Agent' },
            { name: 'isBot', label: 'isBot' },
            { name: 'isBot2', label: 'isBot2 (DeviceDetector)' },
            { name: 'Details', label: 'Details' },
          ]}
          options={{
            filterType: 'checkbox',
            downloadOptions: {
              filename:
                'BotCheck_' + new Date().toISOString().slice(0, 10) + '.csv',
              filterOptions: {
                useDisplayedColumnsOnly: true,
              },
            },
            sort: true,
            sortDirection: 'desc',
            textLabels: {
              body: {
                noMatch: '',
              },
            },
          }}
        />
      </div>
    </div>
  );
}
